import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { Clock, ChefHat, ArrowRight, Play } from 'lucide-react'
import { LocalizedLink } from '@/components/LocalizedLink'

const recipes = [
  {
    id: 1,
    slug: 'pizza-bianca',
    image: '/images/Ricette_652X732-Mortadella.jpg',
    titleKey: 'home.recipes.recipe1.title',
    timeKey: 'home.recipes.recipe1.time',
    difficulty: 'easy',
    category: 'Pizza',
  },
  {
    id: 2,
    slug: 'carbonara',
    image: '/images/Ricette_652X732-Guanciale.jpg',
    titleKey: 'home.recipes.recipe2.title',
    timeKey: 'home.recipes.recipe2.time',
    difficulty: 'medium',
    category: 'Primi',
  },
  {
    id: 3,
    slug: 'crostini',
    image: '/images/Ricette_652X732-Fiordicotto.jpg',
    titleKey: 'home.recipes.recipe3.title',
    timeKey: 'home.recipes.recipe3.time',
    difficulty: 'easy',
    category: 'Antipasti',
  },
]

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.2,
    },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.4, 0, 0.2, 1] as const,
    },
  },
}

export function RecipesSection() {
  const { t } = useTranslation('common')

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy':
        return 'bg-green-100 text-green-700'
      case 'medium':
        return 'bg-yellow-100 text-yellow-700'
      case 'hard':
        return 'bg-red-100 text-red-700'
      default:
        return 'bg-gray-100 text-gray-700'
    }
  }

  return (
    <section className="section-padding bg-white" aria-labelledby="recipes-title">
      <div className="container-fiorucci">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-1.5 bg-[#5C1A1A]/10 text-[#5C1A1A] text-sm font-semibold rounded-full mb-4">
            {t('nav.recipes')}
          </span>
          <h2 id="recipes-title" className="heading-section text-[#4E2118] mb-4">
            {t('home.recipes.title')}
          </h2>
          <p className="text-body-lg text-[#4E2118]/70 max-w-2xl mx-auto mb-4">
            {t('home.recipes.description')}
          </p>
          <p className="text-lg text-[#DF631E] font-serif italic">
            {t('home.recipes.subtitle')}
          </p>
        </motion.div>

        {/* Recipes Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-50px' }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {recipes.map((recipe) => (
            <motion.div key={recipe.id} variants={itemVariants}>
              <LocalizedLink
                to={`recipes/${recipe.slug}`}
                className="group block"
              >
                <article className="relative bg-[#FDF8F3] rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500">
                  {/* Image Container */}
                  <div className="relative aspect-[3/4] overflow-hidden">
                    <img
                      src={recipe.image}
                      alt={t(recipe.titleKey)}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#4E2118] via-[#4E2118]/20 to-transparent" />

                    {/* Category Badge */}
                    <div className="absolute top-4 left-4 px-3 py-1 bg-white/90 backdrop-blur-sm text-[#4E2118] text-xs font-bold rounded-full">
                      {recipe.category}
                    </div>

                    {/* Play Button (Video indicator) */}
                    <div className="absolute top-4 right-4 w-10 h-10 bg-[#DF631E] rounded-full flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                      <Play className="w-4 h-4 text-white fill-white ml-0.5" />
                    </div>

                    {/* Content Overlay */}
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <h3 className="font-serif text-2xl font-bold text-white mb-3 group-hover:text-[#F3E4D6] transition-colors">
                        {t(recipe.titleKey)}
                      </h3>

                      {/* Meta info */}
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-1.5 text-white/90 text-sm">
                          <Clock className="w-4 h-4" />
                          <span>{t(recipe.timeKey)}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <ChefHat className="w-4 h-4 text-white/90" />
                          <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${getDifficultyColor(recipe.difficulty)}`}>
                            {t(`home.recipes.${recipe.difficulty}`)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Bottom Action Bar */}
                  <div className="p-4 bg-white flex items-center justify-between">
                    <span className="text-sm font-semibold text-[#4E2118]">
                      {t('buttons.readMore')}
                    </span>
                    <div className="w-8 h-8 rounded-full bg-[#FDF8F3] flex items-center justify-center group-hover:bg-[#DF631E] transition-colors">
                      <ArrowRight className="w-4 h-4 text-[#4E2118] group-hover:text-white transition-colors" />
                    </div>
                  </div>
                </article>
              </LocalizedLink>
            </motion.div>
          ))}
        </motion.div>

        {/* View All Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mt-12"
        >
          <LocalizedLink
            to="recipes"
            className="btn-primary inline-flex group"
          >
            {t('home.recipes.cta')}
            <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
          </LocalizedLink>
        </motion.div>
      </div>
    </section>
  )
}
